import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { TrendingUp, ArrowRight } from "lucide-react";

const UPLIFT = 0.32;

function formatBRL(value: number) {
  return value.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });
}

interface SliderProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  format: (v: number) => string;
  onChange: (v: number) => void;
}

function Slider({ label, value, min, max, step, format, onChange }: SliderProps) {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <span className="text-sm font-medium text-muted-foreground">{label}</span>
        <span className="text-sm font-bold text-foreground">{format(value)}</span>
      </div>
      <div className="relative h-2 w-full rounded-full bg-secondary">
        <div
          className="absolute left-0 top-0 h-2 rounded-full bg-warm-gradient"
          style={{ width: `${pct}%` }}
        />
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
        />
        <div
          className="absolute top-1/2 h-4 w-4 -translate-y-1/2 rounded-full border-2 border-primary bg-white shadow"
          style={{ left: `calc(${pct}% - 8px)` }}
        />
      </div>
    </div>
  );
}

export function RevenueCalculator() {
  const [ordersPerDay, setOrdersPerDay] = useState(30);
  const [avgTicket, setAvgTicket] = useState(45);

  const currentMonthly = ordersPerDay * avgTicket * 30;
  const gain = Math.round(currentMonthly * UPLIFT);

  return (
    <section className="py-16 md:py-24">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mx-auto max-w-2xl text-center"
        >
          <h2 className="text-3xl font-bold tracking-tight md:text-4xl">
            Quanto você pode ganhar a mais?
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            Restaurantes com cardápio digital vendem em média 32% a mais. Veja o impacto no seu negócio.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="mx-auto mt-10 max-w-3xl overflow-hidden rounded-2xl border bg-card shadow-xl"
        >
          <div className="grid md:grid-cols-2">
            {/* Inputs */}
            <div className="space-y-8 p-6 md:p-8">
              <div>
                <h3 className="text-base font-semibold">Seu negócio hoje</h3>
                <p className="mt-0.5 text-sm text-muted-foreground">Ajuste os valores para o seu contexto.</p>
              </div>

              <Slider
                label="Pedidos por dia"
                value={ordersPerDay}
                min={5}
                max={150}
                step={1}
                format={(v) => `${v} pedidos`}
                onChange={setOrdersPerDay}
              />

              <Slider
                label="Ticket médio"
                value={avgTicket}
                min={15}
                max={150}
                step={5}
                format={(v) => `R$${v}`}
                onChange={setAvgTicket}
              />

              <div className="rounded-xl bg-secondary/60 p-4">
                <p className="text-xs text-muted-foreground">Receita mensal atual estimada</p>
                <p className="mt-1 text-2xl font-bold">{formatBRL(currentMonthly)}</p>
              </div>
            </div>

            {/* Result */}
            <div className="flex flex-col items-center justify-center gap-6 bg-warm-gradient p-6 text-white md:p-8">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/20">
                <TrendingUp className="h-7 w-7 text-white" />
              </div>

              <div className="text-center">
                <p className="text-sm font-medium text-white/80">Ganho potencial por mês</p>
                <motion.p
                  key={gain}
                  initial={{ scale: 1.08, opacity: 0.7 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  className="mt-1 text-4xl font-extrabold tracking-tight"
                >
                  +{formatBRL(gain)}
                </motion.p>
                <p className="mt-2 text-sm text-white/70">com cardápio digital e garçom IA</p>
              </div>

              <div className="w-full space-y-2 rounded-xl bg-white/15 p-4 text-sm">
                <div className="flex justify-between">
                  <span className="text-white/80">Receita atual</span>
                  <span className="font-semibold">{formatBRL(currentMonthly)}</span>
                </div>
                <div className="flex justify-between border-t border-white/20 pt-2">
                  <span className="text-white/80">Com Menuzai</span>
                  <span className="font-bold">{formatBRL(currentMonthly + gain)}</span>
                </div>
              </div>

              <Button
                size="lg"
                className="w-full bg-white text-primary hover:bg-white/90 font-semibold shadow-lg"
                asChild
              >
                <Link to="/entrar">
                  Quero esse resultado <ArrowRight className="ml-1.5 h-4 w-4" />
                </Link>
              </Button>

              <p className="text-center text-xs text-white/60">
                Sem comissão nos pedidos · cancele quando quiser
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
